import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileSpreadsheet,
  CheckCircle2,
  Clock,
  ListChecks,
  ArrowRight,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageContainer, PageHeader } from "@/components/AppLayout";
import { toast } from "sonner";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Admin",
  sme_reviewer: "SME Reviewer",
  coordinator: "Coordinator",
};

function StatCard({ icon: Icon, label, value, color = "text-orange-600", bg = "bg-orange-50" }) {
  return (
    <div className="bg-white rounded-xl p-5 border border-slate-100 brand-shadow">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-slate-400">{label}</div>
          <div
            className="mt-2 text-3xl font-bold text-slate-900 tracking-tight"
            style={{ fontFamily: "Outfit" }}
          >
            {value}
          </div>
        </div>
        <div className={`w-11 h-11 rounded-xl ${bg} ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, unassigned: 0, assigned: 0, in_review: 0, reviewed: 0 });
  const [batches, setBatches] = useState([]);

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: b }] = await Promise.all([
        api.get("/questions/stats"),
        api.get("/batches"),
      ]);
      setStats(s);
      setBatches(b);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const canUpload = ["super_admin", "admin"].includes(user?.role);

  return (
    <PageContainer>
      <PageHeader
        title={`Welcome, ${user?.name || "there"}`}
        subtitle={`Signed in as ${ROLE_LABEL[user?.role] || user?.role}`}
        actions={
          canUpload ? (
            <button
              data-testid="dash-upload-btn"
              onClick={() => navigate("/upload")}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Upload Question Bank
            </button>
          ) : null
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        <StatCard
          icon={FileSpreadsheet}
          label="Total Questions"
          value={stats.total}
          color="text-slate-700"
          bg="bg-slate-100"
        />
        <StatCard
          icon={Clock}
          label="Unassigned"
          value={stats.unassigned}
          color="text-amber-600"
          bg="bg-amber-50"
        />
        <StatCard
          icon={ListChecks}
          label="In Review"
          value={stats.assigned + (stats.in_review || 0)}
          color="text-orange-600"
          bg="bg-orange-50"
        />
        <StatCard
          icon={CheckCircle2}
          label="Reviewed"
          value={stats.reviewed}
          color="text-emerald-600"
          bg="bg-emerald-50"
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-100 brand-shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
              Recent Batches
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Latest reviewer assignments</p>
          </div>
          <button
            onClick={() => navigate("/batches")}
            className="text-sm text-orange-600 hover:text-orange-700 font-semibold inline-flex items-center gap-1"
          >
            View all <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {batches.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-slate-400">
            No batches yet. {canUpload && "Upload a question bank to get started."}
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {batches.slice(0, 6).map((b) => {
              const pct = b.total_questions
                ? Math.round((b.reviewed_count / b.total_questions) * 100)
                : 0;
              return (
                <button
                  key={b.id}
                  onClick={() => navigate(`/batches/${b.id}`)}
                  className="w-full text-left px-6 py-4 hover:bg-slate-50 transition flex items-center gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{b.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5 font-mono">
                      Reviewer: {b.reviewer_name} · {b.total_questions} questions
                    </div>
                  </div>
                  <div className="w-44 hidden sm:block">
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-500 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="text-[11px] font-mono text-slate-400 mt-1 text-right">
                      {b.reviewed_count}/{b.total_questions} reviewed · {pct}%
                    </div>
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-md text-[11px] font-mono uppercase tracking-wider ${
                      b.status === "completed"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-orange-50 text-orange-700"
                    }`}
                  >
                    {b.status}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </PageContainer>
  );
}
